/**
 * StepNotifications — step 4 of the agent-creation flow: never miss a moment.
 *
 * Own component mirroring the Wix onboarding's notifications step: the
 * events x channels matrix (in-app / email), the "also in the portal" chips
 * (Telegram, Slack, Teams) and the email recipients. Saves via
 * PUT /notification_settings/{bot} — the same payload as the notification
 * centre. Continue saves; leaving otherwise saves through the page's
 * before-leave hook.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Checkbox, Tooltip, IconButton, InputAdornment } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faEnvelope, faComments, faHeadset, faUserPlus, faTrash, faPlus, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../../../components/BrandIcons';
import { authFetch } from '../../../utils/api';
import { API, FIELD_SX } from '../steps.js';
import StepFooter from './StepFooter.js';
import './StepNotifications.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHECK_SX = { '&.Mui-checked': { color: '#5616ea' }, '&:hover': { backgroundColor: '#5616ea15' }, '&.Mui-disabled': { color: '#e0e0e6' }, padding: '4px' };

// Structure mirrors the Wix onboarding (and the legacy WixRegister engagement step).
const CHANNELS = [
  { key: 'in_app', icon: faDesktop, labelKey: 'wxo_notif_ch_inapp' },
  { key: 'email', icon: faEnvelope, labelKey: 'wxo_notif_ch_email' },
];
const EVENTS = [
  { key: 'new_chat', section: 'live_chat', icon: faComments, titleKey: 'wxo_notif_chat_title', descKey: 'wxo_notif_chat_desc' },
  { key: 'help_requested', section: 'live_chat', icon: faHeadset, titleKey: 'wxo_notif_help_title', descKey: 'wxo_notif_help_desc', highlight: true },
  { key: 'new_lead', section: 'leads', icon: faUserPlus, titleKey: 'wxo_notif_lead_title', descKey: 'wxo_notif_lead_desc' },
  { key: 'new_message', section: 'live_chat', icon: faEnvelope, titleKey: 'wxo_notif_msg_title', descKey: 'wxo_notif_msg_desc' },
];
const LOCKED = { new_message: ['email'] }; // joined-chat replies: in-app only, no email spam
const isLocked = (ev, ch) => (LOCKED[ev] || []).includes(ch);
const DEFAULT_PREFS = {
  new_chat: { in_app: true, email: false },
  help_requested: { in_app: true, email: true },
  new_lead: { in_app: true, email: true },
  new_message: { in_app: true, email: false },
};

export default function StepNotifications({ botId, goNext, goBack, registerBeforeLeave, notify, strips, prefillEmail = '' }) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [emails, setEmails] = useState(['']);
  const [initial, setInitial] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const stateRef = useRef({}); stateRef.current = { prefs, emails };
  const initialRef = useRef(initial); initialRef.current = initial;

  // ── load ──
  useEffect(() => {
    if (!botId) return undefined;
    let alive = true;
    (async () => {
      let p = DEFAULT_PREFS; let em = ['']; let saved = false;
      try {
        const r = await fetch(`${API}notification_settings/${encodeURIComponent(botId)}`);
        if (r.ok) {
          const d = await r.json();
          if (d.prefs && Object.keys(d.prefs).length) p = { ...DEFAULT_PREFS, ...d.prefs };
          const ebs = d.emails_by_section || {};
          const list = Array.from(new Set([...(ebs.live_chat || []), ...(ebs.leads || [])])).filter(Boolean);
          if (list.length) { em = list.slice(0, 3); saved = true; }
        }
      } catch { /* defaults */ }
      if (!alive) return;
      // A pre-filled address is a suggestion, not saved yet: the baseline stays empty, so Continue saves it.
      setPrefs(p); setEmails(saved || !prefillEmail ? em : [prefillEmail]);
      setInitial(JSON.stringify({ prefs: p, emails: em }));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [botId, prefillEmail]);

  const emailRequired = EVENTS.some((ev) => !isLocked(ev.key, 'email') && Boolean(prefs[ev.key]?.email));
  const cleanEmails = emails.map((e) => String(e).trim()).filter(Boolean);
  const invalidEmails = cleanEmails.filter((e) => !EMAIL_RE.test(e));
  const emailMissing = emailRequired && cleanEmails.length === 0;
  const emailError = emailMissing || invalidEmails.length > 0;

  const save = useCallback(async () => {
    const { prefs: p, emails: em } = stateRef.current;
    const valid = em.map((e) => String(e).trim()).filter((e) => EMAIL_RE.test(e));
    const needs = (sec) => EVENTS.some((ev) => ev.section === sec && !isLocked(ev.key, 'email') && Boolean(p[ev.key]?.email));
    const payload = {
      prefs: p,
      emails_by_section: { live_chat: needs('live_chat') ? valid : [], leads: needs('leads') ? valid : [] },
      slack_webhooks_by_section: { live_chat: [], leads: [] },
      teams_webhooks_by_section: { live_chat: [], leads: [] },
      telegram_chat_ids_by_section: { live_chat: [], leads: [] },
    };
    const r = await authFetch(`${API}notification_settings/${encodeURIComponent(botId)}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (!r || !r.ok) throw new Error('notification save failed');
    setInitial(JSON.stringify({ prefs: p, emails: em }));
  }, [botId]);

  const isDirty = () => initialRef.current && JSON.stringify(stateRef.current) !== initialRef.current;
  const saveIfDirty = useCallback(async () => {
    if (!isDirty()) return true;
    await save();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  useEffect(() => {
    registerBeforeLeave(saveIfDirty);
    return () => registerBeforeLeave(null);
  }, [registerBeforeLeave, saveIfDirty]);

  const continueNext = async () => {
    setAttempted(true);
    if (emailError) return;
    setSaving(true); setErr('');
    try {
      const wasDirty = isDirty();
      await saveIfDirty();
      registerBeforeLeave(null);
      if (wasDirty) notify(t('ac_notif_saved'));
      goNext();
    } catch (e) { console.error(e); setErr(t('ac_notif_save_failed')); }
    finally { setSaving(false); }
  };

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{t('wxo_notif_title')}</h2>
        <p className="ac-sub">{t('wxo_notif_sub')}</p>
      </header>

      <div className="ac-body">
        {loading ? (
          <div className="ac-loading"><span className="ac-spinner" /></div>
        ) : (
          <>
            <div className="ac-notify-matrix">
              <div className="ac-notify-row ac-notify-head">
                <div className="ac-notify-event-col" />
                {CHANNELS.map((ch) => (
                  <div key={ch.key} className="ac-notify-channel-col">
                    <FontAwesomeIcon icon={ch.icon} className="ac-notify-channel-icon" />
                    <span className="ac-notify-channel-label">{t(ch.labelKey)}</span>
                  </div>
                ))}
              </div>
              {EVENTS.map((ev) => {
                const row = prefs[ev.key] || {};
                return (
                  <div key={ev.key} className={`ac-notify-row ${ev.highlight ? 'is-hi' : ''}`}>
                    <div className="ac-notify-event-col">
                      <div className="ac-notify-event-title"><FontAwesomeIcon icon={ev.icon} className="ac-notify-event-icon" />{t(ev.titleKey)}</div>
                      <div className="ac-notify-event-desc">{t(ev.descKey)}</div>
                    </div>
                    {CHANNELS.map((ch) => {
                      const locked = isLocked(ev.key, ch.key);
                      const cb = (
                        <Checkbox checked={locked ? false : Boolean(row[ch.key])} disabled={locked}
                          onChange={() => { setAttempted(false); setPrefs((p) => ({ ...p, [ev.key]: { ...(p[ev.key] || {}), [ch.key]: !p[ev.key]?.[ch.key] } })); }}
                          sx={CHECK_SX} />
                      );
                      return (
                        <div key={ch.key} className="ac-notify-channel-col">
                          {locked ? <Tooltip title={t('wxo_notif_locked')} arrow><span style={{ cursor: 'not-allowed', display: 'inline-flex' }}><span style={{ pointerEvents: 'none' }}>{cb}</span></span></Tooltip> : cb}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="ac-notify-portal">
              <span className="ac-notify-portal-label">{t('wxo_notif_also')}</span>
              <span className="ac-notify-chips">
                <span className="ac-notify-chip"><TelegramBrandIcon size={15} />Telegram</span>
                <span className="ac-notify-chip"><SlackBrandIcon size={15} />Slack</span>
                <span className="ac-notify-chip"><TeamsBrandIcon size={15} />Teams<span className="ac-notify-soon">{t('wxo_notif_soon')}</span></span>
              </span>
            </div>

            {emailRequired && (
              <div className="ac-notify-recipients">
                <div className="ac-notify-recipients-head"><FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} /><span>{t('wxo_notif_recipients')}</span></div>
                <div className="ac-notify-emails">
                  {(emails.length ? emails : ['']).map((value, idx) => {
                    const list = emails.length ? emails : [''];
                    const trimmed = String(value).trim();
                    const showError = attempted && trimmed.length > 0 && !EMAIL_RE.test(trimmed);
                    return (
                      <TextField key={idx} label={t('wxo_notif_email_n', { n: idx + 1 })} value={value}
                        onChange={(e) => { const v = e.target.value; setAttempted(false); setEmails((prev) => { const next = (prev.length ? prev : ['']).slice(); next[idx] = v; return next; }); }}
                        type="text" fullWidth variant="outlined" size="small" error={showError} placeholder="you@example.com"
                        InputProps={{ endAdornment: list.length > 1 ? (<InputAdornment position="end"><IconButton aria-label={t('remove')} edge="end" size="small" onClick={() => { setAttempted(false); setEmails((prev) => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : ['']; }); }}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null }}
                        sx={FIELD_SX} />
                    );
                  })}
                </div>
                {emails.length < 3 && (
                  <button type="button" className="ac-notify-add-email" onClick={() => { setAttempted(false); setEmails((prev) => (prev.length >= 3 ? prev : [...prev, ''])); }} title={t('wxo_notif_add_email')}><FontAwesomeIcon icon={faPlus} /></button>
                )}
                {attempted && emailError && (
                  <div className="ac-notify-error"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{emailMissing ? t('wxo_notif_err_missing') : t('wxo_notif_err_invalid')}</span></div>
                )}
              </div>
            )}

            {err && <div className="ac-error">{err}</div>}
            {strips}
          </>
        )}
      </div>

      <StepFooter onBack={goBack} primaryLabel={t('wxo_notif_continue')} onPrimary={continueNext} busy={saving} />
    </>
  );
}
